<script>
import { mapGetters } from 'vuex';
import ConditionsBuilder from './ConditionsBuilder.vue';
import ConditionRecheckDelay from './ConditionRecheckDelay.vue';
import ActionsBuilder from './ActionsBuilder.vue';
import AutomationEventSelect from './AutomationEventSelect.vue';
import AutomationFormSection from './AutomationFormSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { validateAutomation } from 'dashboard/helper/validations';
import {
  flattenConditions,
  generateAutomationPayload,
} from 'dashboard/helper/automationHelper';
import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES } from './constants';

const start_value = {
  name: null,
  description: null,
  event_name: 'conversation_created',
  recheck_delay_seconds: null,
  conditions: [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ],
  actions: [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ],
};

export default {
  components: {
    ConditionsBuilder,
    ConditionRecheckDelay,
    ActionsBuilder,
    AutomationEventSelect,
    AutomationFormSection,
    NextButton,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['saveAutomation'],
  setup() {
    const {
      automation,
      automationTypes,
      onEventChange,
      appendNewAction,
      removeAction,
      resetAction,
      manifestCustomAttributes,
    } = useAutomation(start_value);
    return {
      automation,
      automationTypes,
      onEventChange,
      appendNewAction,
      removeAction,
      resetAction,
      manifestCustomAttributes,
    };
  },
  data() {
    return {
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationMutated: false,
      show: true,
      showDeleteConfirmationModal: false,
      allCustomAttributes: [],
      mode: 'create',
      errors: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    // Whether anything has been filled in yet, so the form can warn that
    // switching the event throws it away. A sugar trigger may legitimately
    // carry no conditions at all, so nothing here may assume there is a first
    // one.
    hasAutomationMutated() {
      return (
        flattenConditions(this.automation.conditions).some(
          condition => condition.values?.length
        ) ||
        this.automation.actions.some(action => action.action_params?.length)
      );
    },
    automationActionTypes() {
      const actionTypes = this.isFeatureEnabled('sla')
        ? AUTOMATION_ACTION_TYPES
        : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

      return actionTypes.map(action => ({
        ...action,
        label: this.$t(`AUTOMATION.ACTIONS.${action.label}`),
      }));
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('contacts/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('campaigns/get');
    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];
    this.manifestCustomAttributes();
  },
  methods: {
    isFeatureEnabled(flag) {
      return this.isFeatureEnabledonAccount(this.accountId, flag);
    },
    emitSaveAutomation() {
      this.errors = validateAutomation(this.automation);
      if (Object.keys(this.errors).length === 0) {
        const automation = generateAutomationPayload(this.automation);
        this.$emit('saveAutomation', automation, this.mode);
      }
    },
  },
};
</script>

<template>
  <div>
    <woot-modal-header :header-title="$t('AUTOMATION.ADD.TITLE')" />
    <div class="flex flex-col modal-content">
      <div class="w-full">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: errors.name }"
          :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :class="{ error: errors.description }"
          :error="
            errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />

        <AutomationEventSelect
          v-model="automation.event_name"
          :error="errors.event_name"
          :show-reset-warning="hasAutomationMutated"
          @update:model-value="onEventChange(automation)"
        />

        <AutomationFormSection
          :label="$t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL')"
        >
          <ConditionsBuilder
            v-model="automation.conditions"
            :automation-types="automationTypes"
            :automation="automation"
            :all-custom-attributes="allCustomAttributes"
            :mode="mode"
            :errors="errors"
          />
          <ConditionRecheckDelay v-model="automation.recheck_delay_seconds" />
        </AutomationFormSection>

        <AutomationFormSection :label="$t('AUTOMATION.ADD.FORM.ACTIONS.LABEL')">
          <ActionsBuilder
            v-model="automation.actions"
            :action-types="automationActionTypes"
            :errors="errors"
            @reset="resetAction"
            @remove="removeAction"
            @add="appendNewAction"
          />
        </AutomationFormSection>

        <div class="w-full">
          <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
            <NextButton
              faded
              slate
              type="reset"
              :label="$t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT')"
              @click.prevent="onClose"
            />
            <NextButton
              solid
              blue
              type="submit"
              :label="$t('AUTOMATION.ADD.SUBMIT')"
              @click="emitSaveAutomation"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
